import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import FileUpload from "@/components/admin/FileUpload";
import { can, MANAGE_CONTENT } from "@/lib/format";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, ExternalLink, Lock } from "lucide-react";

function Section({ title, children }) {
  return (
    <Card className="border-[var(--border-lch)] p-5">
      <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wide mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

function Field({ label, value, onChange, textarea, rows = 2, disabled, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-[var(--text-2)]">{label}</Label>
      {textarea ? (
        <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[var(--panel)]" data-testid={testId} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[var(--panel)]" data-testid={testId} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, disabled }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs text-[var(--text-2)]">{label}</Label>
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 rounded bg-[var(--panel-2)] overflow-hidden shrink-0 border border-[var(--border-lch)]">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder="Image URL" className="bg-[var(--panel)] flex-1" />
      </div>
      {!disabled && <FileUpload visibility="public" category="content" label="Upload Image" onUploaded={(fs) => fs[0] && onChange(fs[0].url)} />}
    </div>
  );
}

export default function AdminContent() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_CONTENT);
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/content/about");
      setC(r.data);
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  // list helpers
  const setBio = (i, v) => setC((p) => { const a = [...(p.founder_bio || [])]; a[i] = v; return { ...p, founder_bio: a }; });
  const addBio = () => setC((p) => ({ ...p, founder_bio: [...(p.founder_bio || []), ""] }));
  const removeBio = (i) => setC((p) => ({ ...p, founder_bio: (p.founder_bio || []).filter((_, idx) => idx !== i) }));

  const setItem = (key, i, field, v) => setC((p) => { const a = [...(p[key] || [])]; a[i] = { ...a[i], [field]: v }; return { ...p, [key]: a }; });
  const addItem = (key) => setC((p) => ({ ...p, [key]: [...(p[key] || []), { title: "", body: "" }] }));
  const removeItem = (key, i) => setC((p) => ({ ...p, [key]: (p[key] || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/about", c);
      toast.success("About page saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !c) return <div className="text-[var(--muted)] p-8">Loading…</div>;

  return (
    <div data-testid="admin-content" className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <BookOpen className="text-[#c9a86a]" size={22} />
          <div>
            <h1 className="text-2xl font-semibold">About / Founder Story</h1>
            <p className="text-sm text-[var(--text-2)]">Edit the public About page content.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/about" target="_blank" rel="noreferrer" className="text-sm text-[#9a7b3f] hover:opacity-80 transition-opacity flex items-center gap-1" data-testid="view-about-live">
            View live <ExternalLink size={13} />
          </a>
          {!editable && <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)]"><Lock size={11} className="mr-1" />View only</Badge>}
        </div>
      </div>

      <div className="space-y-5">
        <Section title="Hero">
          <Field label="Eyebrow" value={c.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} disabled={!editable} testId="hero-eyebrow" />
          <Field label="Title" value={c.hero_title} onChange={(v) => set("hero_title", v)} disabled={!editable} testId="hero-title" />
          <Field label="Subtitle" value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} disabled={!editable} textarea rows={3} />
          <ImageField label="Hero Background Image" value={c.hero_image} onChange={(v) => set("hero_image", v)} disabled={!editable} />
        </Section>

        <Section title="Founder">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={c.founder_name} onChange={(v) => set("founder_name", v)} disabled={!editable} />
            <Field label="Role" value={c.founder_role} onChange={(v) => set("founder_role", v)} disabled={!editable} />
          </div>
          <ImageField label="Portrait" value={c.founder_portrait} onChange={(v) => set("founder_portrait", v)} disabled={!editable} />
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label className="text-xs text-[var(--text-2)]">Bio Paragraphs</Label>{editable && <Button size="sm" variant="outline" onClick={addBio} data-testid="add-bio"><Plus size={13} className="mr-1" />Paragraph</Button>}</div>
            {(c.founder_bio || []).map((p, i) => (
              <div key={i} className="flex gap-2">
                <Textarea rows={2} value={p} onChange={(e) => setBio(i, e.target.value)} disabled={!editable} className="bg-[var(--panel)]" data-testid={`bio-${i}`} />
                {editable && <Button size="sm" variant="ghost" onClick={() => removeBio(i)} className="text-red-500 shrink-0"><Trash2 size={14} /></Button>}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Philosophy & Values">
          <Field label="Philosophy Title" value={c.philosophy_title} onChange={(v) => set("philosophy_title", v)} disabled={!editable} />
          <Field label="Philosophy Intro" value={c.philosophy_intro} onChange={(v) => set("philosophy_intro", v)} disabled={!editable} textarea rows={3} />
          <div className="flex items-center justify-between"><Label className="text-xs text-[var(--text-2)]">Values</Label>{editable && <Button size="sm" variant="outline" onClick={() => addItem("values")} data-testid="add-value"><Plus size={13} className="mr-1" />Value</Button>}</div>
          {(c.values || []).map((v, i) => (
            <div key={i} className="border border-[var(--border-lch)] rounded p-3 space-y-2 bg-[var(--panel-2)]/60" data-testid={`value-editor-${i}`}>
              <div className="flex gap-2">
                <Input value={v.title} onChange={(e) => setItem("values", i, "title", e.target.value)} disabled={!editable} placeholder="Title" className="bg-[var(--panel)]" />
                {editable && <Button size="sm" variant="ghost" onClick={() => removeItem("values", i)} className="text-red-500 shrink-0"><Trash2 size={14} /></Button>}
              </div>
              <Textarea rows={2} value={v.body} onChange={(e) => setItem("values", i, "body", e.target.value)} disabled={!editable} placeholder="Body" className="bg-[var(--panel)]" />
            </div>
          ))}
        </Section>

        <Section title="Authentication-First">
          <Field label="Title" value={c.auth_title} onChange={(v) => set("auth_title", v)} disabled={!editable} />
          <Field label="Intro" value={c.auth_intro} onChange={(v) => set("auth_intro", v)} disabled={!editable} textarea rows={2} />
          <div className="flex items-center justify-between"><Label className="text-xs text-[var(--text-2)]">Steps</Label>{editable && <Button size="sm" variant="outline" onClick={() => addItem("auth_steps")} data-testid="add-step"><Plus size={13} className="mr-1" />Step</Button>}</div>
          {(c.auth_steps || []).map((s, i) => (
            <div key={i} className="border border-[var(--border-lch)] rounded p-3 space-y-2 bg-[var(--panel-2)]/60" data-testid={`step-editor-${i}`}>
              <div className="flex gap-2">
                <Input value={s.title} onChange={(e) => setItem("auth_steps", i, "title", e.target.value)} disabled={!editable} placeholder="Title" className="bg-[var(--panel)]" />
                {editable && <Button size="sm" variant="ghost" onClick={() => removeItem("auth_steps", i)} className="text-red-500 shrink-0"><Trash2 size={14} /></Button>}
              </div>
              <Textarea rows={2} value={s.body} onChange={(e) => setItem("auth_steps", i, "body", e.target.value)} disabled={!editable} placeholder="Body" className="bg-[var(--panel)]" />
            </div>
          ))}
        </Section>

        <Section title="Pull Quote & CTA">
          <Field label="Pull Quote" value={c.pull_quote} onChange={(v) => set("pull_quote", v)} disabled={!editable} textarea rows={2} />
          <Field label="Quote Attribution" value={c.pull_quote_attrib} onChange={(v) => set("pull_quote_attrib", v)} disabled={!editable} />
          <Field label="CTA Title" value={c.cta_title} onChange={(v) => set("cta_title", v)} disabled={!editable} />
          <Field label="CTA Subtitle" value={c.cta_subtitle} onChange={(v) => set("cta_subtitle", v)} disabled={!editable} textarea rows={2} />
        </Section>

        {editable && (
          <div className="flex justify-end sticky bottom-4">
            <Button onClick={save} disabled={saving} data-testid="content-save" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)] shadow-lg">
              {saving ? "Saving…" : "Save About Page"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
